import React from 'react';
import DOMPurify from 'dompurify';
import { Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

export interface ContentDescriptionProps {
  /** Content object with a description that may contain HTML. */
  content: { description?: string | null };
  /** Line-clamp count. Default 2. Pass `undefined` for no clamp. */
  clamp?: number;
  /** Optional sx overrides. */
  sx?: SxProps<Theme>;
}

/**
 * Renders the content description as plain text, stripped of any HTML.
 * Two-line clamp by default.
 */
export const ContentDescription: React.FC<ContentDescriptionProps> = ({ content, clamp = 2, sx }) => {
  if (!content.description) return null;
  const text = DOMPurify.sanitize(content.description, { ALLOWED_TAGS: [] });
  const clampSx: SxProps<Theme> = clamp
    ? {
        display: '-webkit-box',
        WebkitLineClamp: clamp,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
      }
    : {};
  return (
    <Typography
      sx={[
        {
          fontSize: 13,
          lineHeight: 1.45,
          color: (theme) => theme.palette.text.secondary,
          margin: 0,
        },
        clampSx,
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {text}
    </Typography>
  );
};

export default ContentDescription;
